// C:\Users\phill\paperplay\frontend\src\features\account\store\accountThunks.ts

import { createAsyncThunk } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';

import axiosInstance from '../../../apis/common/axiosInstance';
import type { UUID, PermissionName } from '../../../types/commonTypes';
import type { User, UserActivityDetails } from '../accountTypes';
import type { UserType } from '../../../types/servicesTypes';

import { AUTH } from '../../authorization/authEndpoints';
import { USERS } from '../usersEndpoints';

/* ----------------------------------------------------------------------------
 * Helpers
 * -------------------------------------------------------------------------- */

const getErr = (err: unknown, fallback: string): string => {
  if (isAxiosError(err)) {
    const msg =
      (err.response?.data as { message?: string } | undefined)?.message ??
      err.response?.statusText;
    return msg || fallback;
  }
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message?: string }).message || fallback;
  }
  return fallback;
};

/** Try to unwrap `{ data: T }` or return `T` as-is. */
function unwrap<T>(payload: T | { data: T }): T {
  return (payload && typeof payload === 'object' && 'data' in payload ? (payload as any).data : payload) as T;
}

/** Normalize various backend user shapes into `User` used by AccountState. */
function toUser(raw: unknown): User {
  const src = unwrap<UserType & Partial<Record<string, unknown>>>(raw as any) as any;

  const userId =
    (src?.userId as UUID) ??
    (src?.id as UUID) ??
    (src?.uuid as UUID) ??
    null;

  const avatar = (src?.avatar as string) ?? (src?.profilePicture as string) ?? '';

  const roles =
    (Array.isArray(src?.roles) ? (src.roles as string[]) : []) ||
    (Array.isArray(src?.userRoles) ? (src.userRoles as string[]) : []);

  return {
    userId,
    email: String(src?.email ?? ''),
    roles,
    is_active: Boolean(src?.is_active ?? src?.isActive ?? true),
    is_staff: Boolean(src?.is_staff ?? src?.isStaff ?? false),
    avatar,
    artistProfile: src?.artistProfile,
    venueProfile: src?.venueProfile,
  } as User;
}

/** Convert possibly random list containers to a plain array. */
function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    const candidate =
      (obj.results as T[]) ??
      (obj.items as T[]) ??
      (obj.data as T[]) ??
      (obj.list as T[]);
    return Array.isArray(candidate) ? candidate : [];
  }
  return [];
}

type PermissionLike = {
  codename?: unknown;
  name?: unknown;
};

export function normalizePermissionList(payload: unknown): PermissionName[] {
  const items = unwrapList<unknown>(payload);
  const out: PermissionName[] = [];

  for (const item of items) {
    if (typeof item === 'string' && item.trim()) {
      out.push(item as PermissionName);
      continue;
    }

    if (item && typeof item === 'object') {
      const candidate = item as PermissionLike;
      const codename = typeof candidate.codename === 'string' ? candidate.codename.trim() : '';
      const name = typeof candidate.name === 'string' ? candidate.name.trim() : '';
      const value = codename || name;
      if (value) out.push(value as PermissionName);
    }
  }

  return out;
}

/* ----------------------------------------------------------------------------
 * Thunks
 * -------------------------------------------------------------------------- */

/**
 * Get the current (logged-in) user from the auth endpoint.
 * Accepts common payloads: `UserType` or `{ data: UserType }`.
 */
export const fetchAccountCurrentUser = createAsyncThunk<
  User,
  void,
  { rejectValue: string }
>('account/fetchCurrentUser', async (_: void, thunkApi) => {
  try {
    const resp = await axiosInstance.get<UserType | { data: UserType }>(AUTH.CURRENT_USER, {
      withCredentials: true,
    });
    return toUser(resp.data);
  } catch (err) {
    return thunkApi.rejectWithValue(getErr(err, 'Failed to fetch current user.'));
  }
});

/**
 * Get permissions for a user.
 * Accepts either a raw array or ApiResponse-like containers.
 */
export const fetchAccountUserPermissions = createAsyncThunk<
  PermissionName[],
  { userId: UUID },
  { rejectValue: string }
>('account/fetchUserPermissions', async ({ userId }, thunkApi) => {
  try {
    const resp = await axiosInstance.get<unknown>(USERS.PERMISSIONS(userId));
    return normalizePermissionList(resp.data);
  } catch (err) {
    return thunkApi.rejectWithValue(getErr(err, 'Failed to fetch user permissions.'));
  }
});

/**
 * Get paginated activities for a user.
 * Accepts backends that return arrays or {data, pagination} shapes.
 */
export const fetchAccountUserActivities = createAsyncThunk<
  {
    items: UserActivityDetails[];
    page: number;
    pageSize: number;
    totalPages: number;
    totalCount: number;
  },
  { userId: UUID; page?: number; pageSize?: number; filters?: Record<string, unknown> },
  { rejectValue: string }
>('account/fetchUserActivities', async ({ userId, page = 1, pageSize = 10, filters }, thunkApi) => {
  try {
    const params = {
      page,
      page_size: pageSize,
      ...(filters || {}),
    };

    const resp = await axiosInstance.get<
      | UserActivityDetails[]
      | {
          data?: UserActivityDetails[];
          results?: UserActivityDetails[];
          items?: UserActivityDetails[];
          pagination?: {
            page?: number;
            pageSize?: number;
            page_size?: number;
            totalPages?: number;
            total_pages?: number;
            totalCount?: number;
            total_count?: number;
          };
          totalCount?: number;
          total_pages?: number;
        }
    >(USERS.ACTIVITIES(userId), { params });

    const items = unwrapList<UserActivityDetails>(resp.data);

    // Try to extract pagination if present; otherwise derive something reasonable.
    const obj = (Array.isArray(resp.data) ? {} : (resp.data || {})) as Record<string, any>;
    const p = obj.pagination || {};

    const totalCount = Number(
    obj.totalCount ?? p.totalCount ?? p.total_count ?? items.length ?? 0
    );

    // Avoid mixing ?? and || by splitting the steps
    const totalPagesRaw =
    obj.total_pages ?? p.totalPages ?? Math.ceil(totalCount / pageSize);
    const totalPages = Number.isFinite(Number(totalPagesRaw)) ? Number(totalPagesRaw) : 0;

    const resolvedPage = Number(p.page ?? page);
    const resolvedPageSize = Number(p.pageSize ?? p.page_size ?? pageSize);

    return {
    items,
    page: resolvedPage || 1,
    pageSize: resolvedPageSize || pageSize,
    totalPages,
    totalCount: totalCount || items.length || 0,
    };

  } catch (err) {
    return thunkApi.rejectWithValue(getErr(err, 'Failed to fetch user activities.'));
  }
});
